import React, { useEffect, useState } from 'react';
import UpdateSettingsForm from './UpdateSettingsForm';
import { getDealerSettings, updateDealerSettings, updateDealerNegotiationRules, updateDealerCRMSettings } from '../../services/dealer';
import DotsLoading from '../ui/DotsLoading';
import { apiAuth } from '../../config/api';

const defaultValues = {
    name: '',
    email: '',
    mc_dealer_id: '',
    mc_website_id: '',
    address: {
        street: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        latitude: '',
        longitude: ''
    },
    phone: '',
    inventoryUrl: '',
    website: '',
    leads_email: '',
    backup_email: '',
    backup_email2: '',
    support_email: '',
    disclaimer: '',
    negotiationRules: [''],
    crmEndpoint: '',
    crmApiKey: ''
};

const UpdateSettingsFormContainer = () => {
    const [values, setValues] = useState(defaultValues);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);


    useEffect(() => {
        setIsLoading(true);
        getDealerSettings()
            .then(data => data?.data)
            .then(data => {
                console.log(data);
                if (data && data.dealer) {
                    const dealer = data.dealer;
                    const dealerInfo = dealer.dealerInfo || {};
                    
                    const initialValues = {
                        name: dealerInfo.name || `${dealer.first_name || ''} ${dealer.last_name || ''}`.trim() || '',
                        email: dealer.email || '',
                        mc_dealer_id: dealer.mc_dealer_id || '',
                        mc_website_id: dealer.mc_website_id || '',
                        address: {
                            street: dealer.address || dealerInfo.street || '',
                            city: dealer.city || dealerInfo.city || '',
                            state: dealer.state || dealerInfo.state || '',
                            zip: dealer.postal_code || dealerInfo.zip || '',
                            country: dealerInfo.country || 'United States',
                            latitude: dealer.latitude || dealerInfo.latitude || '',
                            longitude: dealer.longitude || dealerInfo.longitude || ''
                        },
                        phone: dealer.dealer_phone_number || dealerInfo.phone || '',
                        inventoryUrl: dealer.inventoryUrl || dealerInfo.inventoryUrl || '',
                        website: dealer.website || '',
                        leads_email: dealer.leads_email || '',
                        backup_email: dealer.backup_email || '',
                        backup_email2: dealer.backup_email2 || '',
                        support_email: dealer.support_email || '',
                        disclaimer: dealer.disclaimer || '',
                        negotiationRules: dealerInfo.negotiationRules && dealerInfo.negotiationRules.length > 0 ? dealerInfo.negotiationRules : [''],
                        crmEndpoint: dealerInfo.crmEndpoint || '',
                        crmApiKey: dealerInfo.crmApiKey || ''
                    }
                    setValues(initialValues);
                }
            })
            .catch(() => setError('Failed to load settings'))
            .finally(() => setIsLoading(false));
    }, []);

    const handleSubmit = async (values, onSuccess) => {
        setIsSubmitting(true);
        setError('');
        setSuccess('');
        try {
            // Prepare main dealer settings payload (excluding negotiation rules and dealerInfo)
            const payload = {
                first_name: values.name.split(' ')[0] || '',
                last_name: values.name.split(' ').slice(1).join(' ') || '',
                email: values.email,
                dealer_phone_number: values.phone,
                address: values.address.street,
                city: values.address.city,
                state: values.address.state,
                postal_code: values.address.zip,
                latitude: values.address.latitude ? parseFloat(values.address.latitude) : null,
                longitude: values.address.longitude ? parseFloat(values.address.longitude) : null,
                mc_dealer_id: values.mc_dealer_id,
                mc_website_id: values.mc_website_id,
                inventoryUrl: values.inventoryUrl,
                website: values.website,
                leads_email: values.leads_email,
                backup_email: values.backup_email,
                backup_email2: values.backup_email2,
                support_email: values.support_email,
                disclaimer: values.disclaimer
            };

            // Update main dealer settings
            const res = await updateDealerSettings(payload);
            if (res && res.status === 'success') {
                let successMessage = 'Settings updated successfully!';
                
                // Update negotiation rules separately
                try {
                    const negotiationRes = await updateDealerNegotiationRules(values.negotiationRules);
                    if (negotiationRes && negotiationRes.status === 'success') {
                        successMessage += ' Negotiation rules updated successfully!';
                    } else {
                        successMessage += ' Failed to update negotiation rules.';
                    }
                } catch (negotiationErr) {
                    successMessage += ' Failed to update negotiation rules.';
                }
                
                // Update CRM settings separately
                try {
                    const crmRes = await updateDealerCRMSettings(values.crmEndpoint, values.crmApiKey);
                    if (crmRes && crmRes.status === 'success') {
                        successMessage += ' CRM settings updated successfully!';
                    } else {
                        successMessage += ' Failed to update CRM settings.';
                    }
                } catch (crmErr) {
                    successMessage += ' Failed to update CRM settings.';
                }
                
                setSuccess(successMessage);
                onSuccess();
            } else {
                setError(res.message || 'Failed to update settings');
            }
        } catch (err) {
            setError('Failed to update settings');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <DotsLoading />;
    }

    return (
        <UpdateSettingsForm
            values={values}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            error={error}
            success={success}
        />

    );
};

export default UpdateSettingsFormContainer; 